import { useMemo } from "react"
import PropTypes from "prop-types"
import { chain, capitalize, keyBy, map, get, keys, find } from "lodash"
import classNames from "classnames"
import { Paper } from "@mui/material"
import { ROLE, PERMISSION_LEVEL, TOKEN } from "helpers/shapes"
import { PERMISSION_LEVELS, PERMISSION_LEVELS_AS_OPTIONS, hasPermission } from "helpers/permissions"
import { formatLongDate } from "helpers/formatters/dates"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import Table from "components/material/table"

export default function ResourceTokens({ account, resourceId, resourceType, permissionLevel, role, resourceTokens, accountTokens, update, isOpPro, tabs }) {
  const hasWriteAccess = role === "admin" || hasPermission(permissionLevel, PERMISSION_LEVELS.OWNER)
  const nonEditable = isOpPro

  const tokensById = useMemo(() => keyBy(accountTokens, "id"), [accountTokens])
  const availableOptionLabels = useMemo(() =>
    chain(accountTokens)
      .filter(({ id }) => !find(resourceTokens, m => m.id === id))
      .keyBy("id")
      .mapValues(m => m.name)
      .value(),
  [accountTokens, resourceTokens])

  const addForm = useMemo(() => {
    if (!hasWriteAccess || nonEditable) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        ${map(data.tokens, (tokenId, index) => `token${index}: updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { tokenId: "${tokenId}", permissionLevel: $permissionLevel }) { id }`)}
      }`

      const variables = { permissionLevel: data.permissionLevel, resourceId, resourceType }

      update(query, variables, account, {
        reloadData: "resourceTokens",
      }, cb)
    }

    return {
      title: "Add Tokens",
      submitText: "Add Token",
      track: {
        type: "resource-permissions-add-token",
        payload: ({ tokens, permissionLevel }) => ({
          tokens: map(tokens, id => get(tokensById, [id, "name"], id)),
          permissionLevel,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "tokens",
        label: "Tokens",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }, {
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, nonEditable, availableOptionLabels, resourceId, resourceType, update, account, tokensById])

  const editForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onEdit(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { tokenId: "${data.id}", permissionLevel: $permissionLevel }) { id }
      }`

      const variables = {
        resourceId,
        resourceType,
        permissionLevel: data.permissionLevel,
      }

      update(query, variables, account, {
        reloadData: "resourceTokens",
      }, cb)
    }

    return {
      title: "Edit Permission Level",
      submitText: "Save",
      track: {
        type: "resource-permissions-edit-token",
        payload: ({ id, permissionLevel }) => ({
          token: get(tokensById, [id, "name"], id),
          permissionLevel,
        }),
      },
      isEnabled: () => hasWriteAccess,
      onSubmit: onEdit,
      getInitialValues: ({ permissionLevel }) => ({ permissionLevel }),
      fields: [{
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [resourceId, resourceType, update, account, hasWriteAccess, tokensById])

  const deleteForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!) {
        ${map(data, (token, index) => `token${index}: updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { tokenId: "${token.id}", permissionLevel: NONE }) { id }`)}
      }`

      const variables = { resourceId, resourceType }

      update(query, variables, account, {
        reloadData: "resourceTokens",
      }, cb)
    }

    return {
      type: "permission",
      track: {
        type: "resource-permissions-remove-token",
        payload: ({ name }) => ({ token: name }),
      },
      isEnabled: () => hasWriteAccess,
      renderItem: resource => <li key={resource.id}>{resource.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, resourceId, resourceType, update])

  return <div className={classNames("modules_resource-tokens", "with-header")}>
    <UpsellTabs id="resource-tokens" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Resource Permissions"
        idField="id"
        addForm={addForm}
        editForm={editForm}
        deleteForm={deleteForm}
        data={resourceTokens}
        columns={[{
          name: "Name",
          id: "name",
          linkTo: token => `/${account}/home/tokens/${token.id}`,
        }, {
          name: "Created",
          id: "createdAt",
          formatter: formatLongDate,
        }, {
          name: "Updated",
          id: "updatedAt",
          formatter: formatLongDate,
        }, {
          name: "Permission Level",
          id: "permissionLevel",
          formatter: capitalize,
        }]}
      />
    </Paper>
  </div>
}

ResourceTokens.propTypes = {
  account: PropTypes.string.isRequired,
  role: ROLE.isRequired,
  permissionLevel: PERMISSION_LEVEL,
  resourceId: PropTypes.string,
  resourceType: PropTypes.string,
  resourceTokens: PropTypes.arrayOf(TOKEN),
  accountTokens: PropTypes.arrayOf(TOKEN),
  update: PropTypes.func.isRequired,
  isOpPro: PropTypes.bool.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
